import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import EventIcon from "@mui/icons-material/Event";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import LinkIcon from "@mui/icons-material/Link";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import SavingsIcon from "@mui/icons-material/Savings";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import GfgModal from "@/components/GfgModal";
import { SCHEDULE_MODE_LABELS } from "@/constants/campaignBuilder";
import { CONFIGS as MODAL_TEXT, recipientCapCopy } from "@/constants/GfgModal";
import type { GfgModalContext, ModalTrigger } from "@/context/CampaignBuilderContext";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useRecipientCap } from "@/hooks/useRecipientCap";
import type { ContextConfig, ModalData } from "@/interfaces/GfgModal";

const CONFIGS: Record<GfgModalContext, ContextConfig> = {
  international: {
    icon: <LanguageIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.international.title,
    description: () => MODAL_TEXT.international.description,
    primaryLabel: MODAL_TEXT.international.primaryLabel,
    secondaryLabel: MODAL_TEXT.international.secondaryLabel,
    primaryFirst: true,
    primaryIcon: <LockOpenIcon />,
  },
  edit: {
    icon: <LockOpenIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.edit.title,
    description: () => MODAL_TEXT.edit.description,
    primaryLabel: MODAL_TEXT.edit.primaryLabel,
    secondaryLabel: MODAL_TEXT.edit.secondaryLabel,
    primaryFirst: true,
    primaryIcon: <LockOpenIcon />,
  },
  addMoreGifts: {
    icon: <InfoOutlinedIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.addMoreGifts.title,
    description: ({ addMoreTier, selectedBudget }) => (
      <Typography
        sx={{
          fontFamily: "var(--font-lato)",
          fontSize: "16px",
          color: "var(--color-graphite)",
          textAlign: "center",
          lineHeight: "normal",
        }}
      >
        {`You're adding $${addMoreTier} gifts to your $${selectedBudget} collection. You'll pay the $${selectedBudget} tier upfront, but if a recipient picks a $${addMoreTier} gift, `}
        <Box
          component="span"
          sx={{ fontWeight: 600, fontSize: "18px", color: "var(--color-rodeo-black)" }}
        >
          {`only $${addMoreTier} will be deducted from your balance`}
        </Box>
        {"."}
      </Typography>
    ),
    primaryLabel: MODAL_TEXT.addMoreGifts.primaryLabel,
    secondaryLabel: MODAL_TEXT.addMoreGifts.secondaryLabel,
  },
  unsavedChanges: {
    icon: <WarningAmberIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.unsavedChanges.title,
    description: () => MODAL_TEXT.unsavedChanges.description,
    primaryLabel: MODAL_TEXT.unsavedChanges.primaryLabel,
    secondaryLabel: MODAL_TEXT.unsavedChanges.secondaryLabel,
  },
  recipientCapWarning: {
    icon: <GroupAddIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.recipientCapWarning.title,
    description: ({ capCount }) => MODAL_TEXT.recipientCapWarning.description(capCount ?? 0),
    primaryLabel: MODAL_TEXT.recipientCapWarning.primaryLabel,
    secondaryLabel: MODAL_TEXT.recipientCapWarning.secondaryLabel,
    primaryIcon: <LockOpenIcon />,
  },
  recipientCapBlocked: {
    icon: <GroupAddIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.recipientCapBlocked.title,
    description: () => MODAL_TEXT.recipientCapBlocked.description,
    primaryLabel: MODAL_TEXT.recipientCapBlocked.primaryLabel,
    secondaryLabel: MODAL_TEXT.recipientCapBlocked.secondaryLabel,
    primaryIcon: <LockOpenIcon />,
  },
  schedulePerRecipient: {
    icon: <EventIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.schedulePerRecipient.title,
    description: () => MODAL_TEXT.schedulePerRecipient.description,
    primaryLabel: MODAL_TEXT.schedulePerRecipient.primaryLabel,
    secondaryLabel: MODAL_TEXT.schedulePerRecipient.secondaryLabel,
    primaryIcon: <LockOpenIcon />,
  },
  giftReminders: {
    icon: <ScheduleIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.giftReminders.title,
    description: () => MODAL_TEXT.giftReminders.description,
    primaryLabel: MODAL_TEXT.giftReminders.primaryLabel,
    secondaryLabel: MODAL_TEXT.giftReminders.secondaryLabel,
    primaryIcon: <LockOpenIcon />,
  },
  manualLinks: {
    icon: <ForwardToInboxIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.manualLinks.title,
    description: () => MODAL_TEXT.manualLinks.description,
    primaryLabel: MODAL_TEXT.manualLinks.primaryLabel,
    secondaryLabel: MODAL_TEXT.manualLinks.secondaryLabel,
    primaryIcon: <LockOpenIcon />,
  },
  universalLinks: {
    icon: <LinkIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.universalLinks.title,
    description: () => MODAL_TEXT.universalLinks.description,
    primaryLabel: MODAL_TEXT.universalLinks.primaryLabel,
    secondaryLabel: MODAL_TEXT.universalLinks.secondaryLabel,
    primaryIcon: <LockOpenIcon />,
  },
  payg: {
    icon: <SavingsIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.payg.title,
    description: () => MODAL_TEXT.payg.description,
    primaryLabel: MODAL_TEXT.payg.primaryLabel,
    secondaryLabel: MODAL_TEXT.payg.secondaryLabel,
    primaryIcon: <LockOpenIcon />,
  },
  landingBrandColors: {
    icon: <PaletteOutlinedIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.landingBrandColors.title,
    description: () => MODAL_TEXT.landingBrandColors.description,
    primaryLabel: MODAL_TEXT.landingBrandColors.primaryLabel,
    secondaryLabel: MODAL_TEXT.landingBrandColors.secondaryLabel,
    primaryIcon: <LockOpenIcon />,
  },
  landingConfetti: {
    icon: <CelebrationOutlinedIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.landingConfetti.title,
    description: () => MODAL_TEXT.landingConfetti.description,
    primaryLabel: MODAL_TEXT.landingConfetti.primaryLabel,
    secondaryLabel: MODAL_TEXT.landingConfetti.secondaryLabel,
    primaryIcon: <LockOpenIcon />,
  },
  shortExpiryWarning: {
    icon: <WarningAmberIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.shortExpiryWarning.title,
    description: ({ shortExpiryDays }) => (
      <Typography
        sx={{
          fontFamily: "var(--font-lato)",
          fontSize: "16px",
          color: "var(--color-graphite)",
          textAlign: "center",
          lineHeight: "normal",
        }}
      >
        {`Are you sure you only want your campaign to run for ${shortExpiryDays ?? 0} day${
          shortExpiryDays === 1 ? "" : "s"
        }? We recommend leaving your campaign live for 60 days, which allows everyone time to redeem their gift, and for your gift reminder emails to be sent.`}
      </Typography>
    ),
    primaryLabel: MODAL_TEXT.shortExpiryWarning.primaryLabel,
    secondaryLabel: MODAL_TEXT.shortExpiryWarning.secondaryLabel,
  },
  // Manual gift links — informational ack. Single button ("Got it"); the copy is NOT rewritten,
  // this only names the recipient tags that will render blank on the unboxing page.
  strandedTags: {
    icon: <WarningAmberIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.strandedTags.title,
    description: ({ strandedTags }) => {
      const tags = strandedTags ?? [];
      const list =
        tags.length <= 1
          ? (tags[0] ?? "")
          : `${tags.slice(0, -1).join(", ")} and ${tags[tags.length - 1]}`;
      const clause = tags.length === 1 ? "this tag will appear" : "these tags will appear";
      return `Your landing page uses ${list}, which fills in from a recipient list. Gift links aren't tied to a recipient, so ${clause} exactly as written. Update your landing page message before you launch.`;
    },
    primaryLabel: MODAL_TEXT.strandedTags.primaryLabel,
  },
  // GFG-443 — the sender chooses between the file's dates and the schedule already picked; nothing
  // is imported until they do.
  sendDatesConflict: {
    icon: <EventIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.sendDatesConflict.title,
    description: ({ scheduleLabel = SCHEDULE_MODE_LABELS.asap }) =>
      `Your file includes individual send dates, but this campaign is set to "${scheduleLabel}". Switch to "${SCHEDULE_MODE_LABELS.different}" to send each recipient on their own date, or keep "${scheduleLabel}" and import the file without its dates.`,
    primaryLabel: MODAL_TEXT.sendDatesConflict.primaryLabel,
    secondaryLabel: MODAL_TEXT.sendDatesConflict.secondaryLabel,
    primaryFirst: true,
  },
  // GFG-443 — informational ack: each default the import or manual add applied, one per line.
  sendDefaultsApplied: {
    icon: <ScheduleIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: MODAL_TEXT.sendDefaultsApplied.title,
    description: ({ noticeLines }) => (
      <ul className="m-0 pl-5 text-left font-lato text-[15px] text-graphite space-y-1.5">
        {(noticeLines ?? []).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    ),
    primaryLabel: MODAL_TEXT.sendDefaultsApplied.primaryLabel,
  },
};

export default function CampaignBuilderModal() {
  const { modal, closeModal, selectedBudget } = useCampaignBuilder();

  // Freeze context + data during the close animation to prevent content flash
  const [stableModal, setStableModal] = useState<ModalTrigger | null>(modal);
  const [stableData, setStableData] = useState<ModalData>({
    addMoreTier: modal?.addMoreTier,
    selectedBudget,
    addedCount: modal?.addedCount,
    capCount: modal?.capCount,
    shortExpiryDays: modal?.shortExpiryDays,
    strandedTags: modal?.strandedTags,
    scheduleLabel: modal?.scheduleLabel,
    noticeLines: modal?.noticeLines,
  });

  useEffect(() => {
    if (modal !== null) {
      setStableModal(modal);
      setStableData({
        addMoreTier: modal.addMoreTier,
        selectedBudget,
        addedCount: modal.addedCount,
        capCount: modal.capCount,
        shortExpiryDays: modal.shortExpiryDays,
        strandedTags: modal.strandedTags,
        scheduleLabel: modal.scheduleLabel,
        noticeLines: modal.noticeLines,
      });
    }
  }, [modal, selectedBudget]);

  const context = stableModal?.context ?? "international";
  const { cap, planKey } = useRecipientCap();
  const capCopy = planKey && cap !== null ? recipientCapCopy(planKey, cap) : null;
  const baseConfig = CONFIGS[context];
  // The cap modals name the caller's own plan and cap; every other context is static.
  const config: ContextConfig =
    capCopy && context === "recipientCapBlocked"
      ? {
          ...baseConfig,
          ...capCopy.blocked,
          description: () => capCopy.blocked.description,
        }
      : capCopy && context === "recipientCapWarning"
        ? {
            ...baseConfig,
            ...capCopy.warning,
            description: ({ capCount }) => capCopy.warning.description(capCount ?? 0),
          }
        : baseConfig;
  const description = config.description(stableData);

  return (
    <GfgModal
      open={modal !== null}
      onClose={closeModal}
      icon={config.icon}
      title={config.title}
      description={description}
      primaryLabel={config.primaryLabel}
      secondaryLabel={config.secondaryLabel}
      primaryFirst={config.primaryFirst}
      primaryIcon={config.primaryIcon}
      dismissible={stableModal?.dismissible ?? true}
      onPrimary={stableModal?.onPrimary ?? closeModal}
      onSecondary={stableModal?.onSecondary}
      onDismiss={stableModal?.onDismiss}
    />
  );
}
